/**
 * A rule group — the root ("People who match ALL of these") or a nested one —
 * with its ALL/ANY toggle, its rules and sub-groups, and its add and remove
 * actions. Ported from the concept's `segments/RuleGroup.jsx`.
 *
 * ## WHAT IT KEEPS FROM THE CONCEPT
 *
 * The join is a `SegmentedToggle` labelled ALL/AND and ANY/OR, and between
 * every two children the join word is repeated — the one thing that makes a
 * nested tree readable aloud. A nested group is a bordered panel (violet for
 * AND, amber for OR) and the third level is dashed, so depth is visible without
 * indentation eating a phone's width. A nested group shows its own count: the
 * people the group matches on its own.
 *
 * ## WHAT THE CONTRACT ADDS
 *
 * The limits are the contract's, checked before a button is offered rather than
 * after a 400: twenty children per group, sixty rules in all, groups three deep
 * (`RULE_MAX_*`). A button that cannot work is disabled and says why beside it
 * — a hidden button teaches nobody where the limit is.
 *
 * Removing a group's last rule leaves the group empty rather than deleting it
 * under the person's cursor; an empty group says so, matches nothing on save
 * (it is dropped — `toRuleTree`), and has its own remove button.
 *
 * Owns no state. {@link useRuleTreeEditor} holds the tree and hands out the
 * four actions; the Flow builder uses rows without groups, and the segment
 * builder uses both.
 */
import { RULE_MAX_CHILDREN, RULE_MAX_LEAVES, type Field, type FieldDateFormat, type RuleJoin } from '@wend/shared';
import { NoFigure, SegmentedToggle, cn } from '@wend/ui';
import { Group, Plus, X } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import { RULE_ADD_BUTTON_CLASS, RULE_REMOVE_BUTTON_CLASS } from './controls';
import { RuleRow } from './RuleRow';
import {
  canAddChild,
  canNestInside,
  draftLeaves,
  makeGroup,
  makeRule,
  nextRuleField,
  removeNode,
  updateNode,
  type DraftGroup,
  type DraftNode,
  type DraftRule,
  type FieldIndex,
} from './rule-model';

const EN_IN = new Intl.NumberFormat('en-IN');

export const JOIN_OPTIONS = [
  { value: 'and', label: 'ALL', sub: 'AND' },
  { value: 'or', label: 'ANY', sub: 'OR' },
] as const;

/* -------------------------------------------------------------------------- */
/* The editor: state and actions                                               */
/* -------------------------------------------------------------------------- */

export interface RuleTreeActions {
  readonly setRule: (rule: DraftRule) => void;
  readonly setJoin: (groupId: string, join: RuleJoin) => void;
  readonly remove: (id: string) => void;
  readonly addRule: (groupId: string) => void;
  readonly addGroup: (groupId: string) => void;
}

/**
 * The tree and its edits. `index` is read at call time for "which field does a
 * new rule start on", so a catalogue that arrives after the first render is the
 * one new rules use.
 */
export function useRuleTreeEditor(
  initial: DraftGroup | (() => DraftGroup),
  index: FieldIndex | null,
): readonly [DraftGroup, RuleTreeActions, (tree: DraftGroup) => void] {
  const [tree, setTree] = useState<DraftGroup>(initial);

  const newRule = useCallback(
    (current: DraftGroup): DraftRule | null => {
      if (index === null) return null;
      const field = nextRuleField(index, current);
      return field === null ? null : makeRule(field);
    },
    [index],
  );

  const actions = useMemo<RuleTreeActions>(
    () => ({
      setRule: (rule) => setTree((current) => updateNode(current, rule.id, () => rule)),
      setJoin: (groupId, join) =>
        setTree((current) => updateNode(current, groupId, (node) => (node.kind === 'group' ? { ...node, join } : node))),
      remove: (id) => setTree((current) => removeNode(current, id)),
      addRule: (groupId) =>
        setTree((current) => {
          const rule = newRule(current);
          if (rule === null) return current;
          return updateNode(current, groupId, (node) => (node.kind === 'group' ? { ...node, children: [...node.children, rule] } : node));
        }),
      addGroup: (groupId) =>
        setTree((current) => {
          const rule = newRule(current);
          if (rule === null) return current;
          return updateNode(current, groupId, (node) =>
            node.kind === 'group'
              ? { ...node, children: [...node.children, makeGroup(node.join === 'and' ? 'or' : 'and', [rule])] }
              : node,
          );
        }),
    }),
    [newRule],
  );

  return [tree, actions, setTree] as const;
}

/* -------------------------------------------------------------------------- */
/* The group                                                                   */
/* -------------------------------------------------------------------------- */

export interface RuleGroupProps {
  readonly node: DraftGroup;
  /** The whole tree — for the limits that count across it. */
  readonly root: DraftGroup;
  /** 0 for the root. */
  readonly depth?: number;
  readonly fields: readonly Field[];
  /** Per-rule and per-group counts from the preview, by id. */
  readonly counts: ReadonlyMap<string, number>;
  readonly counting?: boolean;
  /** The catalogue's objections, by rule id. */
  readonly issues: ReadonlyMap<string, string>;
  readonly actions: RuleTreeActions;
  /** False when the catalogue offers nothing a new rule could start on. */
  readonly canAdd: boolean;
  readonly onChooseDateFormat?: (key: string, format: FieldDateFormat) => Promise<void>;
  readonly disabled?: boolean;
}

export function RuleGroup({
  node,
  root,
  depth = 0,
  fields,
  counts,
  counting = false,
  issues,
  actions,
  canAdd,
  onChooseDateFormat,
  disabled = false,
}: RuleGroupProps) {
  const size = node.children.length;
  const word = node.join === 'and' ? 'AND' : 'OR';
  const roomHere = canAddChild(node, root);
  const nestable = canNestInside(depth);
  const groupCount = counts.get(node.id);
  const limitNote =
    node.children.length >= RULE_MAX_CHILDREN
      ? `A group holds ${String(RULE_MAX_CHILDREN)} conditions at most.`
      : draftLeaves(root).length >= RULE_MAX_LEAVES
        ? `A segment holds ${String(RULE_MAX_LEAVES)} rules at most.`
        : null;

  return (
    <div
      data-group-id={node.id}
      data-join={node.join}
      className={cn(
        'min-w-0 rounded-base',
        depth > 0 && 'border px-3 pt-2.5 pb-3',
        depth > 0 && node.join === 'and' && 'border-brand-b/32 bg-[linear-gradient(180deg,rgba(109,94,246,.08),rgba(109,94,246,.02))]',
        depth > 0 && node.join === 'or' && 'border-warn/38 bg-[linear-gradient(180deg,rgba(245,184,81,.07),rgba(245,184,81,.015))]',
        depth > 1 && 'border-dashed',
      )}
    >
      <div className="mb-2.5 flex flex-wrap items-center gap-x-2.5 gap-y-2">
        <span className="inline-flex items-center gap-1.5 text-[0.88rem] font-semibold text-text">
          {depth > 0 && <Group aria-hidden="true" className="size-4 text-brand-b" />}
          {depth === 0 ? 'People who match' : 'Group'}
        </span>
        <SegmentedToggle
          size="sm"
          tone={node.join === 'or' ? 'ident-c' : 'brand'}
          label={depth === 0 ? 'Match all or any of the conditions' : 'Match all or any of this group'}
          value={node.join}
          options={JOIN_OPTIONS}
          onValueChange={(value) => actions.setJoin(node.id, value === 'or' ? 'or' : 'and')}
        />
        <span className="text-[0.84rem] text-muted">
          of {size === 1 ? 'this condition' : `these ${String(size)} conditions`}
        </span>
        {depth > 0 && (
          <span className="ml-auto inline-flex items-center gap-1.5">
            <span
              data-role="group-count"
              className={cn(
                'whitespace-nowrap rounded-full border border-line bg-white/5 px-2.5 py-0.5 font-mono text-[0.72rem] tabular-nums text-muted',
                counting && 'opacity-60',
              )}
            >
              {groupCount === undefined ? (
                <NoFigure state="awaiting" meaning="Counting this group" />
              ) : (
                <>
                  <span aria-hidden="true">{EN_IN.format(groupCount)}</span>
                  <span className="sr-only">{`${EN_IN.format(groupCount)} people match this group on its own`}</span>
                </>
              )}
            </span>
            <button type="button" className={RULE_REMOVE_BUTTON_CLASS} aria-label="Remove this group and its rules" onClick={() => actions.remove(node.id)} disabled={disabled}>
              <X aria-hidden="true" className="size-4" />
            </button>
          </span>
        )}
      </div>

      <div>
        {size === 0 && (
          <p className="my-1 text-[0.82rem] italic text-muted">
            {depth === 0
              ? 'No conditions yet. Add a rule to say who belongs in this segment.'
              : 'This group is empty, so it is left out. Add a rule to it, or remove it.'}
          </p>
        )}
        {node.children.map((child: DraftNode, index) => (
          <div key={child.id} className="min-w-0">
            {index > 0 && (
              <div
                aria-hidden="true"
                className={cn(
                  'relative py-[5px] pl-4 font-mono text-[0.64rem] tracking-[0.16em]',
                  "before:absolute before:top-0 before:bottom-0 before:left-[7px] before:w-px before:content-['']",
                  node.join === 'and' ? 'text-brand-b before:bg-brand-b/40' : 'text-warn before:bg-warn/45',
                )}
              >
                {word}
              </div>
            )}
            {child.kind === 'group' ? (
              <RuleGroup
                node={child}
                root={root}
                depth={depth + 1}
                fields={fields}
                counts={counts}
                counting={counting}
                issues={issues}
                actions={actions}
                canAdd={canAdd}
                onChooseDateFormat={onChooseDateFormat}
                disabled={disabled}
              />
            ) : (
              <RuleRow
                rule={child}
                fields={fields}
                count={counts.get(child.id)}
                counting={counting}
                issue={issues.get(child.id)}
                onChange={actions.setRule}
                onRemove={() => actions.remove(child.id)}
                onChooseDateFormat={onChooseDateFormat}
                disabled={disabled}
              />
            )}
          </div>
        ))}
      </div>

      <div className={cn('mt-2.5 flex flex-wrap items-center gap-2', depth === 0 && 'mt-3.5 border-t border-dashed border-line pt-3')}>
        <button type="button" className={RULE_ADD_BUTTON_CLASS} onClick={() => actions.addRule(node.id)} disabled={disabled || !canAdd || !roomHere}>
          <Plus aria-hidden="true" className="size-4" />
          {depth === 0 ? 'Add rule' : `Add rule to this ${word} group`}
        </button>
        {nestable && (
          <button type="button" className={RULE_ADD_BUTTON_CLASS} onClick={() => actions.addGroup(node.id)} disabled={disabled || !canAdd || !roomHere}>
            <Group aria-hidden="true" className="size-4" />
            {depth === 0 ? 'Add group' : 'Add group inside'}
          </button>
        )}
        {depth === 0 && size > 0 && (
          <span className="text-[0.74rem] text-muted">
            {node.join === 'and' ? 'A person must match every condition above.' : 'A person must match at least one condition above.'}
          </span>
        )}
        {limitNote !== null && <span className="text-[0.74rem] text-warn">{limitNote}</span>}
        {!canAdd && depth === 0 && (
          <span className="text-[0.74rem] text-warn">This workspace has no field a rule can use yet. Upload a list first.</span>
        )}
      </div>
    </div>
  );
}
